import {
    Expression,
    ExpressionPath,
    Literal,
    defaultOp,
    defaultLogOp,
    LogOp,
    ConditionOp,
    FilterPath,
    isExpressionPath,
    isLiteral,
    ExpressionFunction,
    PathType,
    typesMap,
    genericOps,
    stringOps,
    dateTimeOps,
} from './types';

// import { findBoolExpression, findParent } from './reducer';
import { hasText } from 'src/utils';
import _ from 'lodash';

export const getNewExpression: () => Expression = () => ({
    lhs: { path: '' },
    op: defaultOp,
    rhs: [{ path: '' }],
    items: [],
    ops: [],
    negated: false,
    selected: false,
})

export const isSimple: (exp: Expression) => boolean = (exp) => exp.items.length == 0;

export const getFilter: (exp: Expression) => string = (exp) => {
    if (isSimple(exp)) {
        const { lhs, op, rhs, negated } = exp;
        const notEmpty = lhs.path != '';
        return `${negated ? 'not ' : ''}${notEmpty ? '(' : ''}${serializeExpressionPath(lhs.path!)} ${notEmpty ? op.name + (op.arity > 0 ? ' ' : '') : ''}${op.arity > 0 ? serializeRhsExpressionPaths(rhs) : ''}${notEmpty ? ')' : ''}`;
    }
    const { ops, items } = exp;
    const tokens = items.reduce((acc, item, index) => {
        acc.push(getFilter(item));
        if (index < ops.length) {
            acc.push(ops[index] == LogOp.and ? 'and' : 'or');
        }
        return acc;
    }, [] as string[]);

    return (exp.negated ? 'not ' : '') + '(' + tokens.join(' ') + ')';
}

const serializeExpressionPath: (path: string | ExpressionPath | Literal) => string = (path) => {
    if (isExpressionPath(path)) {
        const expPath: ExpressionPath = path as ExpressionPath;
        if (expPath.func) {
            const func: ExpressionFunction = expPath.func;
            const serArgs = func.args.map(arg => serializeExpressionPath(arg));
            return `${serializeExpressionPath(expPath.path)}.${func.spec.name}(${serArgs.join(', ')})`;
        }
        return serializeExpressionPath(expPath.path);
    } else if (isLiteral(path)) {
        return `"${(path as Literal).value}"`;
    } else {
        return path as string;
    }
}

const serializeRhsExpressionPaths: (paths: ExpressionPath[]) => string = (paths) => {
    return paths.map(path => serializeExpressionPath(path)).join(', ');
}

export const pushItem: (exp: Expression, item: Expression, logOp?: LogOp) => void = (exp, item, logOp = defaultLogOp) => {
    if (isSimple(exp)) {
        const { negated, lhs, op, rhs, selected, items, ops } = exp;
        reset(exp);
        exp.items.push({ negated, lhs, op, rhs, selected, items, ops });
    }
    exp.items.push(item);
    exp.ops.push(logOp);
}

export const unshiftItem: (exp: Expression, item: Expression) => void = (exp, item) => {
    if (isSimple(exp)) {
        const { negated, lhs, op, rhs, selected, items, ops } = exp;
        reset(exp);
        exp.items.push(item);
        exp.items.push({ negated, lhs, op, rhs, selected, items, ops });
        exp.ops.push(defaultLogOp);
    } else {
        exp.items.unshift(item);
        exp.ops.unshift(defaultLogOp);
    }
}

export const insertAfter: (exp: Expression, index: number, item: Expression) => void = (exp, index, item) => {
    if (isSimple(exp)) {
        pushItem(exp, item);
    } else {
        exp.items.splice(index + 1, 0, item);
        exp.ops.splice(index, 0, defaultLogOp);
    }
}

export const insertBefore: (exp: Expression, index: number, item: Expression) => void = (exp, index, item) => {
    if (isSimple(exp)) {
        unshiftItem(exp, item);
    } else {
        exp.items.splice(index, 0, item);
        if (index > 0) {
            exp.ops.splice(index - 1, 0, defaultLogOp);
        } else {
            exp.ops.unshift(defaultLogOp);
        }
    }
}

export const removeItem: (exp: Expression, index: number) => void = (exp, index) => {
    if (exp.items.length == 2) {
        const remaining = exp.items[index == 0 ? 1 : 0];
        reset(exp);
        if (isSimple(remaining)) {
            const { negated, lhs, op, rhs } = remaining;
            exp.lhs = lhs;
            exp.op = op;
            exp.rhs = rhs;
            exp.negated = negated;
        } else {
            exp.items = remaining.items;
            exp.ops = remaining.ops;
        }
    } else {
        exp.items.splice(index, 1);
        exp.ops.splice(index == 0 ? 0 : index - 1, 1);
    }
}

export const setOp: (exp: Expression) => void = (exp) => {

}

export const reset: (exp: Expression) => void = (exp) => {
    exp.items = [];
    exp.ops = [];
    exp.lhs = { path: '' };
    exp.op = defaultOp;
    exp.rhs = [{ path: '' }];
    exp.negated = false;
    exp.selected = false;
}

export const group: (exp: Expression) => void = (exp) => {

    const selected = exp.items.reduce((acc, item, index) => {
        if (item.selected) {
            acc.push(index);
        }
        return acc;
    }, [] as number[]);

    if (selected.length > 1) {
        const group: Expression = getNewExpression();
        const firstIndex: number = selected.shift()!;
        const firstSelItem = exp.items[firstIndex];
        firstSelItem.selected = false;
        group.items.push(firstSelItem);

        selected.forEach(selIndex => {
            const selItem = exp.items[selIndex];
            selItem.selected = false;
            pushItem(group, selItem, exp.ops[selIndex - 1]);
        });
        exp.items.splice(firstIndex, 1, group);

        let count = 0;
        selected.forEach(selIndex => {
            exp.items.splice(selIndex - count, 1);
            exp.ops.splice(selIndex - count - 1, 1);
            count++;
        });
    }
}

export const findBoolExpression: (
    expression: Expression,
    path: FilterPath
) => Expression = (expression, path) => {
    if (path.length == 0) {
        return expression;
    }
    const p = [...path];
    const index = p.shift();
    return findBoolExpression(expression.items[index!], p);
}

export const findParent: (expression: Expression, path: FilterPath) => Expression = (expression, path) => {
    if (path.length == 0) {
        throw 'cannot find parent for an expression with no parent';
    }
    return findBoolExpression(expression, _.initial(path));
}

export const explode: (exp: Expression, path: FilterPath) => void = (exp, path) => {
    const index = _.last(path)!;
    const exploded = findBoolExpression(exp, path);
    const parent = findParent(exp, path);
    parent.items.splice(index, 1, ...exploded.items);
    parent.ops.splice(index, 0, ...exploded.ops);
}

export const getReturnType: (expPath: ExpressionPath) => PathType | null = (expressionPath) => {
    if (expressionPath.field) {
        return typesMap[expressionPath.field.type];
    }
    if (isExpressionPath(expressionPath.path) && expressionPath.path.func) {
        return expressionPath.path.func.spec.returns;
    }
    return null;
}

export const getOps: (expPath: ExpressionPath) => ConditionOp[] = (expPath) => {
    const ops: ConditionOp[] = [];
    const pathType: PathType | null = getReturnType(expPath);
    if (pathType != null) {
        ops.push(...genericOps);
        if (pathType == PathType.String) {
            ops.push(...stringOps);
        } else if (pathType == PathType.Date) {
            ops.push(...dateTimeOps);
        }
    }
    return ops;
}
